import {
  Editor,
  IndexKey,
  T,
  TLAsset,
  TLBaseBinding,
  TLBaseShape,
  TLBinding,
  TLShape,
} from "tldraw";

import { compileLabelerLabel } from "@/lib/labeler-logic";
import type {
  AudienceOnEmpty,
  BlendableLensFacet,
  LensOrdering,
} from "@/lib/lens";
import {
  DependencyIntent,
  FeedParameter,
  FilterTypes,
  ManifestNodeMetadata,
  SortingOption,
  SortingParams,
  Thresholds,
} from "@/lib/types";

// 🔴 FOUR FIELDS WERE REMOVED on 2026-09-09. Do not add them back without evidence that
// the data changed -- they were offered here and could NEVER resolve, measured over 48,559
// real enriched posts:
//
//   inferences.video.audio_transcription.text / .language
//       `inferences` resolves on 100% of posts and carries only images, text and
//       text_embeddings. `inferences.video` resolved on 0 of 48,559. Video transcription
//       is not attached to posts.
//   hydrated_metadata.quote_post.author.followers_count
//   hydrated_metadata.parent_post.author.followers_count
//       Those parents resolved 2,441 and 19,093 times and never carried a count of any
//       kind: an embedded post's author is a ProfileViewBasic. Only the post's OWN author
//       gets a full profile fetch, which is why `hydrated_metadata.user.followers_count`
//       below is real and stays.
//
// A condition on a field that cannot resolve is silently dead -- false for every post, so
// a dead branch, or under `and` the whole feed dark; and true for every post when negated,
// so an exclusion that excludes nothing. 13 live feeds read these four, and one owner had
// built a feed called "Test Author Followers" trying to make one of them work.
//
// Removing an option does NOT break the feeds that stored one: `CanvasSimpleSelect`
// (components/Inputs.tsx) unshifts a stored value that is not in `options`, so those nodes
// keep rendering and saving. It just stops being offered to new authors.
// `ai_feed_design.CANVAS_FIELD_PATHS` mirrors this list and was cut to match, which is what
// stops the feed designer proposing them.
export const fields = [
  "text",
  // Synthetic: not a key on the post record, but the instant the post reached the network.
  // See the `fieldOptions` entry below for why it is not called `indexed_at`.
  "arrived_at",
  // Synthetic too, and a NUMBER rather than a date, so it can be a ratio operand.
  "account_age_days",
  "reply",
  "embed",
  "embed.external.uri",
  "embed.external.title",
  "embed.external.description",
  "embed.media.external.uri",
  "embed.media.external.title",
  "embed.media.external.description",
  "embed.alt",
  "embed.images[*].alt",
  "embed.media.images[*].alt",
  "embed.record.uri",
  "langs[*]",
  "labels.values[*].val",
  "facets[*].features[*].uri",
  // User fields
  "hydrated_metadata.user.did",
  "hydrated_metadata.user.handle",
  "hydrated_metadata.user.display_name",
  "hydrated_metadata.user.description",
  "hydrated_metadata.user.followers_count",
  "hydrated_metadata.user.follows_count",
  "hydrated_metadata.user.posts_count",
  "hydrated_metadata.user.created_at",
  "hydrated_metadata.user.labels[*].val",
  "hydrated_metadata.user.verification.verified_status",
  "hydrated_metadata.user.associated.chat.allow_incoming",
  // Mentions fields (accessed by wildcard since keys are DIDs)
  "hydrated_metadata.mentions[*].handle",
  "hydrated_metadata.mentions[*].display_name",
  "hydrated_metadata.mentions[*].followers_count",
  "hydrated_metadata.mentions[*].description",
  "hydrated_metadata.mentions[*].created_at", // ADDED
  // Quote post fields
  "hydrated_metadata.quote_post.author.handle",
  "hydrated_metadata.quote_post.author.display_name",
  "hydrated_metadata.quote_post.author.verification.verified_status",
  "hydrated_metadata.quote_post.record.text",
  "hydrated_metadata.quote_post.like_count",
  "hydrated_metadata.quote_post.reply_count",
  "hydrated_metadata.quote_post.repost_count",
  // Parent post fields (replies)
  "hydrated_metadata.parent_post.author.handle",
  "hydrated_metadata.parent_post.author.display_name",
  "hydrated_metadata.parent_post.record.text",
  "hydrated_metadata.parent_post.like_count",
  // Reply/root post fields
  "hydrated_metadata.reply_post.author.handle",
  "hydrated_metadata.reply_post.record.text",
  "hydrated_metadata.reply_post.like_count",
] as const;

export const textOperators: ComparisonOperator[] = ["==", "!="];
export const numberOperators: ComparisonOperator[] = [
  ">=",
  "<=",
  ">",
  "<",
  "==",
  "!=",
];
const existsOperators: ComparisonOperator[] = ["==", "!="];
export const dateOperators: ComparisonOperator[] = [
  "after",
  "before",
  "newer_than_seconds",
  "older_than_seconds",
  "==",
  "!=",
];

export const fieldOptions: {
  label: string;
  value: string;
  validOperators: ComparisonOperator[];
  /** Shown in the field picker's tooltip. For fields whose NAME does not settle what they
   *  mean -- which is every case where two fields could plausibly be the same thing. */
  hint?: string;
}[] = [
  { label: "Text", value: "text", validOperators: textOperators },
  // The post's OWN timestamp, as distinct from the two account-creation dates further
  // down. It was the one piece of post metadata with no way to filter on it -- not
  // because it was hard, but because it was never listed here.
  //
  // Author-supplied and validated by nothing upstream, so the engines coerce it
  // defensively: anything they cannot read matches nothing rather than reporting
  // itself as 1970 (feed-processor #88, rust-smasher #82).
  {
    label: "Post Created Date",
    value: "createdAt",
    validOperators: dateOperators,
    hint: "When the post says it was written, according to the app that made it. Bridged and misconfigured accounts can be hours out. Use Post Arrival Date to filter on when it actually showed up.",
  },
  // THE SECOND DATE, and deliberately a second one rather than a redefinition of the first.
  // `createdAt` is written by whatever client made the post and is validated by nothing
  // upstream; this is stamped by our own firehose consumer when the record arrived. They
  // are different facts and both are worth filtering on -- `createdAt` is the only way to
  // ask "does the author CLAIM this is old", which is how backdated posts are caught.
  //
  // Reported 2026-09-03 by a feed author whose 10pm-4am feed kept admitting afternoon
  // posts from `*.brid.gy` accounts: 40.9% of bridged posts sit >10min from their arrival
  // (p95 4.2h) against 0.70% of everything else. The Bluesky client shows arrival, which is
  // why the feed and the app appeared to disagree about the same post.
  //
  // NOT called "Indexed Date"/`indexed_at`: that name already means the AUTHOR PROFILE's
  // index time elsewhere in this list, and Graze's per-algorithm match time in ClickHouse.
  // Nor is it named for Bluesky's own `indexedAt` -- it tracks it to a measured median of
  // 0.02s, but it is our observation and not theirs.
  {
    label: "Post Arrival Date",
    value: "arrived_at",
    validOperators: dateOperators,
    hint: "When the post reached the network, stamped by Graze as it arrived rather than by the app that posted it. This is the timestamp the Bluesky app shows, and it is the one to use for time-of-day feeds.",
  },
  // ACCOUNT AGE AS A NUMBER, which is what makes "posts per day" expressible:
  // `ratio_compare` needs two numeric operands and the only account-age field was a date,
  // so a posting-rate filter could not be written at all. Asked for as "ratio of # of posts
  // to days of account existing (yes i'm tryna get bots outta here)".
  //
  // Measured to the post's ARRIVAL, never to now(), so the value never changes: Debug and
  // the matcher agree on it forever, and a post does not become less bot-like as its author
  // ages. Fractional, so an account hours old is 0.25 rather than a zero denominator.
  {
    label: "Account Age (days)",
    value: "account_age_days",
    validOperators: numberOperators,
    hint: "How old the account was, in days, when this post arrived — fractional, so six hours is 0.25. Pair it with User Post Count in a Ratio Comparison to filter on posting rate. It does not change as the post gets older.",
  },
  { label: "Reply", value: "reply", validOperators: existsOperators },
  { label: "Embed", value: "embed", validOperators: existsOperators },
  {
    label: "Link Card URL",
    value: "embed.external.uri",
    validOperators: textOperators,
  },
  {
    label: "Link Card Title",
    value: "embed.external.title",
    validOperators: textOperators,
  },
  {
    label: "Link Card Description",
    value: "embed.external.description",
    validOperators: textOperators,
  },
  {
    label: "Link Media URL",
    value: "embed.media.external.uri",
    validOperators: textOperators,
  },
  {
    label: "Link Media Title",
    value: "embed.media.external.title",
    validOperators: textOperators,
  },
  {
    label: "Link Media Description",
    value: "embed.media.external.description",
    validOperators: textOperators,
  },
  {
    label: "Video Alt Text",
    value: "embed.alt",
    validOperators: textOperators,
  },
  {
    label: "Alt Text",
    value: "embed.images[*].alt",
    validOperators: textOperators,
  },
  {
    label: "Media Alt Text",
    value: "embed.media.images[*].alt",
    validOperators: textOperators,
  },
  {
    label: "Internal Bluesky URL",
    value: "embed.record.uri",
    validOperators: textOperators,
  },
  { label: "Language", value: "langs[*]", validOperators: textOperators },
  // TWO KINDS OF LABEL, and they are not interchangeable. This one is what the
  // AUTHOR put on THIS POST, which in practice is only the adult-content set,
  // because that is all a post is allowed to self-label. Account-level labels --
  // `bot`, `!no-unauthenticated`, `spam` -- live on the profile record and are
  // "User Labels" below.
  //
  // Named "Self Labels" until 2026-09-22, one word away from "User Labels", and a
  // creator trying to keep bots out reached for this one and got a filter that
  // matched nothing: a bot account does not stamp `bot` on each of its posts.
  // Their stopgap (`Self Labels != bot`) was true for every post and excluded
  // nothing, which is worse than an error because it looks like it works.
  {
    label: "Post Self Labels",
    value: "labels.values[*].val",
    validOperators: textOperators,
    hint: "What the author marked THIS POST as — in practice only porn, sexual, nudity or graphic-media, because that is all a post may self-label. For `bot` and other account-level labels use User Labels.",
  },
  {
    label: "Facets Features URI",
    value: "facets[*].features[*].uri",
    validOperators: textOperators,
  },
  // User profile fields
  {
    label: "User DID",
    value: "hydrated_metadata.user.did",
    validOperators: textOperators,
  },
  {
    label: "User Handle",
    value: "hydrated_metadata.user.handle",
    validOperators: textOperators,
  },
  {
    label: "User Display Name",
    value: "hydrated_metadata.user.display_name",
    validOperators: textOperators,
  },
  {
    label: "User Bio",
    value: "hydrated_metadata.user.description",
    validOperators: textOperators,
  },
  {
    label: "User Followers",
    value: "hydrated_metadata.user.followers_count",
    validOperators: numberOperators,
  },
  {
    label: "User Following",
    value: "hydrated_metadata.user.follows_count",
    validOperators: numberOperators,
  },
  {
    label: "User Post Count",
    value: "hydrated_metadata.user.posts_count",
    validOperators: numberOperators,
  },
  {
    label: "User Created Date",
    value: "hydrated_metadata.user.created_at",
    validOperators: dateOperators,
  },
  {
    label: "User Labels",
    value: "hydrated_metadata.user.labels[*].val",
    validOperators: textOperators,
    hint: "Labels on the ACCOUNT, not on the post — `bot`, `!no-unauthenticated`, `spam`, `bridged-from-bridgy-fed-activitypub`. This is the one to use to filter bots out.",
  },
  {
    label: "User Verification",
    value: "hydrated_metadata.user.verification.verified_status",
    validOperators: existsOperators,
  },
  {
    label: "User Chat Settings",
    value: "hydrated_metadata.user.associated.chat.allow_incoming",
    validOperators: existsOperators,
  },
  // Mentions fields
  {
    label: "Mentioned User Handles",
    value: "hydrated_metadata.mentions[*].handle",
    validOperators: textOperators,
  },
  {
    label: "Mentioned User Names",
    value: "hydrated_metadata.mentions[*].display_name",
    validOperators: textOperators,
  },
  {
    label: "Mentioned User Followers",
    value: "hydrated_metadata.mentions[*].followers_count",
    validOperators: numberOperators,
  },
  {
    label: "Mentioned User Bios",
    value: "hydrated_metadata.mentions[*].description",
    validOperators: textOperators,
  },
  {
    label: "Mentioned User Created Date",
    value: "hydrated_metadata.mentions[*].created_at",
    validOperators: dateOperators,
  },
  // Quote post fields
  {
    label: "Quoted Author Handle",
    value: "hydrated_metadata.quote_post.author.handle",
    validOperators: textOperators,
  },
  {
    label: "Quoted Author Name",
    value: "hydrated_metadata.quote_post.author.display_name",
    validOperators: textOperators,
  },
  {
    label: "Quoted Author Verified",
    value: "hydrated_metadata.quote_post.author.verification.verified_status",
    validOperators: existsOperators,
  },
  {
    label: "Quoted Post Text",
    value: "hydrated_metadata.quote_post.record.text",
    validOperators: textOperators,
  },
  {
    label: "Quoted Post Likes",
    value: "hydrated_metadata.quote_post.like_count",
    validOperators: numberOperators,
  },
  {
    label: "Quoted Post Replies",
    value: "hydrated_metadata.quote_post.reply_count",
    validOperators: numberOperators,
  },
  {
    label: "Quoted Post Reposts",
    value: "hydrated_metadata.quote_post.repost_count",
    validOperators: numberOperators,
  },
  // Parent post fields (for replies)
  {
    label: "Parent Author Handle",
    value: "hydrated_metadata.parent_post.author.handle",
    validOperators: textOperators,
  },
  {
    label: "Parent Author Name",
    value: "hydrated_metadata.parent_post.author.display_name",
    validOperators: textOperators,
  },
  {
    label: "Parent Post Text",
    value: "hydrated_metadata.parent_post.record.text",
    validOperators: textOperators,
  },
  {
    label: "Parent Post Likes",
    value: "hydrated_metadata.parent_post.like_count",
    validOperators: numberOperators,
  },
  // Thread root fields
  {
    label: "Thread Root Author",
    value: "hydrated_metadata.reply_post.author.handle",
    validOperators: textOperators,
  },
  {
    label: "Thread Root Text",
    value: "hydrated_metadata.reply_post.record.text",
    validOperators: textOperators,
  },
  {
    label: "Thread Root Likes",
    value: "hydrated_metadata.reply_post.like_count",
    validOperators: numberOperators,
  },
];
export type Field = (typeof fields)[number];

export interface Dimensions {
  w: number;
  h: number;
  minW?: number;
}

export interface BaseShapeProps extends Dimensions, ManifestNodeMetadata {
  resultState?: "failure" | "success" | "idle";
  isReadOnly?: boolean;
  isCollapsed?: boolean;
}

export const regexMatch = ["match", "exclude", "any", "none"] as const;
export type RegexMatch = (typeof regexMatch)[number];
export const simpleRegexType = "regex";
export interface SimpleRegexProps extends BaseShapeProps {
  var: Field;
  regex: string;
  type: "match" | "exclude" | "any" | "none";
  insensitive?: boolean;
}
export const multiRegexType = "multi-regex";
export interface MultiRegexProps extends BaseShapeProps {
  var: Field;
  regex: string[];
  type: "match" | "exclude" | "any" | "none";
  insensitive?: boolean;
  regex_match?: boolean;
  /** `regex_any`/`regex_none`'s fifth slot. Absent = the engines' default. See `lib/term-boundary.ts`. */
  separator?: string;
}
function isMultiRegex(
  shape: SimpleRegexProps | MultiRegexProps,
): shape is MultiRegexProps {
  return "regex" in shape && Array.isArray(shape.regex);
}
export type SimpleRegexShape = TLBaseShape<"element", SimpleRegexProps>;
export type MultiRegexShape = TLBaseShape<"element", MultiRegexProps>;
export interface MatchFilter extends ManifestNodeMetadata {
  regex_matches: [string, string, boolean];
}
export interface ExclusionMatchFilter extends ManifestNodeMetadata {
  regex_negation_matches: [string, string, boolean];
}
export interface AnyMatchFilter extends ManifestNodeMetadata {
  regex_any: [string, string[], boolean, boolean, string?];
}
export interface NoneMatchFilter extends ManifestNodeMetadata {
  regex_none: [string, string[], boolean, boolean, string?];
}
/**
 * The wire arguments for `regex_any`/`regex_none`. The fifth slot is written only
 * when the node sets one, so a node that never touched the setting saves exactly
 * the four-slot manifest it always did.
 */
function termListArgs(
  options: MultiRegexProps,
): [string, string[], boolean, boolean, string?] {
  const args: [string, string[], boolean, boolean, string?] = [
    options.var,
    options.regex,
    // In the manifest true mean insensitive so we flip it here
    options.insensitive ?? true,
    options.regex_match ?? false,
  ];
  if (options.separator !== undefined) {
    args.push(options.separator);
  }
  return args;
}
export function makeRegexFilter(
  options: SimpleRegexProps | MultiRegexProps,
): MatchFilter | ExclusionMatchFilter | AnyMatchFilter | NoneMatchFilter {
  if (isMultiRegex(options)) {
    switch (options.type) {
      case "any":
        return {
          metadata: options.metadata,
          regex_any: termListArgs(options),
        };
      case "none":
        return {
          metadata: options.metadata,
          regex_none: termListArgs(options),
        };
      default:
        throw new Error(`Unsupported type: ${options.type as string}`);
    }
  } else {
    switch (options.type) {
      case "match":
        return {
          metadata: options.metadata,
          regex_matches: [
            options.var,
            options.regex,
            options.insensitive ?? true,
          ],
        };
      case "exclude":
        return {
          metadata: options.metadata,
          regex_negation_matches: [
            options.var,
            options.regex,
            options.insensitive ?? true,
          ],
        };
      default:
        throw new Error(`Unsupported type: ${options.type as string}`);
    }
  }
}

export function isRegexFilter(filter: FilterTypes): filter is MatchFilter {
  return "regex_matches" in filter;
}
export function isRegexNegationFilter(
  filter: FilterTypes,
): filter is ExclusionMatchFilter {
  return "regex_negation_matches" in filter;
}

export function isRegexAnyFilter(
  filter: FilterTypes,
): filter is AnyMatchFilter {
  return "regex_any" in filter;
}
export function isRegexNoneFilter(
  filter: FilterTypes,
): filter is NoneMatchFilter {
  return "regex_none" in filter;
}
export const embedOperators = [
  "image",
  "link",
  "post",
  "image_group",
  "video",
  "gif",
  "gallery",
] as const;
export const embedOperatorOptions = [
  { label: "Link", value: "link" as const },
  { label: "Quote Post", value: "post" as const },
  // "Image" matches both app.bsky.embed.images and app.bsky.embed.gallery, so feeds
  // built before galleries existed keep working. "Gallery" targets galleries alone.
  { label: "Image", value: "image" as const },
  { label: "Gallery", value: "gallery" as const },
  { label: "Image Group", value: "image_group" as const },
  { label: "Gif", value: "gif" as const },
  { label: "Video", value: "video" as const },
];
export type EmbedOperator = (typeof embedOperators)[number];
export const equalityOperators = ["==", "!="] as const;
export const equalityOperatorOptions = [
  { label: "contains", value: "==" as const },
  { label: "missing", value: "!=" as const },
];
export type EqualityOperator = (typeof equalityOperators)[number];
export const comparisonOperators = [
  ">=",
  "<=",
  ">",
  "<",
  "before",
  "after",
  "newer_than_seconds",
  "older_than_seconds",
  ...equalityOperators,
] as const;
export const comparisonOperatorOptions = [
  { label: "at least", value: ">=" as const },
  { label: "at most", value: "<=" as const },
  { label: "above", value: ">" as const },
  { label: "below", value: "<" as const },
  { label: "is", value: "==" as const },
  { label: "not", value: "!=" as const },
  { label: "before", value: "before" as const },
  { label: "after", value: "after" as const },
  { label: "newer than seconds", value: "newer_than_seconds" as const },
  { label: "older than seconds", value: "older_than_seconds" as const },
];
export const percentComparisonOperatorOptions = [
  { label: "at least", value: ">=" as const },
  { label: "at most", value: "<=" as const },
];
export type ComparisonOperator = (typeof comparisonOperators)[number];

export const embedType = "embed-node";
export interface EmbedProps extends BaseShapeProps {
  operator: EqualityOperator;
  embed_name: EmbedOperator;
}
export type EmbedShape = TLBaseShape<"element", EmbedProps>;
export interface EmbedFilter extends ManifestNodeMetadata {
  embed_type: [EqualityOperator, EmbedOperator];
}
export function makeEmbedFilter(options: EmbedProps): EmbedFilter {
  return {
    metadata: options.metadata,
    embed_type: [options.operator, options.embed_name],
  };
}
export function isEmbedFilter(filter: FilterTypes): filter is EmbedFilter {
  return "embed_type" in filter;
}

export const attributeCompareType = "attribute-compare";
export interface AttributeCompareProps extends BaseShapeProps {
  var: Field;
  operator: ComparisonOperator;
  value: string | number | null;
}
export type AttributeCompareShape = TLBaseShape<
  "element",
  AttributeCompareProps
>;
export const ratioCompareType = "ratio-compare";

// Only ORDERING comparators. `==`/`!=` are absent on purpose: the value compared is a
// COMPUTED float, so equality against a threshold almost never holds -- a filter that looks
// reasonable and returns an empty feed. rust-smasher DECLINES such an algorithm at load, so
// offering them here would only let someone author a feed that cannot run.
export const ratioComparisonOperators = [">=", "<=", ">", "<"] as const;
export type RatioComparisonOperator = (typeof ratioComparisonOperators)[number];

// Fields a ratio can be built from: the numeric ones. Dividing a handle by a description is
// not a thing, and `resolve_path` coerces a non-numeric field to 0 rather than refusing, so
// offering the others would produce silent zeroes instead of an error.
export const ratioFieldOptions = fieldOptions.filter(
  (option) => option.validOperators === numberOperators,
);

export interface RatioCompareProps extends BaseShapeProps {
  numerator: Field;
  denominator: Field;
  operator: RatioComparisonOperator;
  value: string | number | null;
}
export type RatioCompareShape = TLBaseShape<"element", RatioCompareProps>;
export interface RatioCompareFilter extends ManifestNodeMetadata {
  ratio_compare: [
    string,
    string,
    RatioComparisonOperator,
    number | string | null,
  ];
}
export function makeRatioCompareFilter(
  options: RatioCompareProps,
): RatioCompareFilter {
  return {
    ratio_compare: [
      options.numerator,
      options.denominator,
      options.operator,
      options.value ?? null,
    ],
    metadata: options.metadata,
  };
}

export interface AttributeCompareFilter extends ManifestNodeMetadata {
  attribute_compare: [string, ComparisonOperator, string | null];
}
export function makeAttributeCompareFilter(
  options: AttributeCompareProps,
): AttributeCompareFilter {
  let value = options.value || null;

  if (value === "null") {
    value = null;
  }

  if (value === null) {
    if (
      options.var === "text" ||
      options.var === "embed.alt" ||
      options.var === "embed.images[*].alt"
    ) {
      value = "";
    }
  }

  return {
    metadata: options.metadata,
    attribute_compare: [options.var, options.operator, value as string],
  };
}
export function isAttributeCompareFilter(
  filter: FilterTypes,
): filter is AttributeCompareFilter {
  return "attribute_compare" in filter;
}

export function isRatioCompareFilter(
  filter: FilterTypes,
): filter is RatioCompareFilter {
  return "ratio_compare" in filter;
}


// --- time windows: hour-of-day and date range ------------------------------------------
//
// `after`/`before` compare whole INSTANTS, so they can say "before Tuesday" but never "in
// the evening, any day". These two cover the rest.

export const hourOfDayBetweenType = "hour-of-day-between";
export const dateBetweenType = "date-between";

// Only the timestamp fields. Asking for "the evening" of a follower count is not a thing,
// and the engines answer false for a field they cannot read as a time, so offering the rest
// would produce silently empty feeds rather than an error.
export const timestampFieldOptions = fieldOptions.filter(
  (option) => option.validOperators === dateOperators,
);

// Hours as clock labels rather than 0-23. An author thinks "6pm", not "18".
export const hourOptions = Array.from({ length: 24 }, (_, hour) => ({
  label:
    hour === 0
      ? "12am"
      : hour < 12
        ? `${hour}am`
        : hour === 12
          ? "12pm"
          : `${hour - 12}pm`,
  value: `${hour}`,
}));

// FIXED OFFSETS, LABELLED AS OFFSETS -- deliberately not named zones like "US Eastern".
// The engines take a fixed number of minutes, so a named zone would be a lie for half the
// year: US Eastern is UTC-5 in winter and UTC-4 in summer, and a feed labelled with the
// name would quietly shift by an hour at each DST change. Naming the offset is honest about
// what the filter actually does.
//
// The fractional entries are real zones, not padding: India is +5:30, Nepal +5:45, and the
// Chatham Islands +12:45. An offset list of whole hours only cannot express any of them.
export const utcOffsetOptions = [
  ["-12:00", -720], ["-11:00", -660], ["-10:00", -600], ["-09:30", -570],
  ["-09:00", -540], ["-08:00", -480], ["-07:00", -420], ["-06:00", -360],
  ["-05:00", -300], ["-04:00", -240], ["-03:30", -210], ["-03:00", -180],
  ["-02:00", -120], ["-01:00", -60], ["+00:00", 0], ["+01:00", 60],
  ["+02:00", 120], ["+03:00", 180], ["+03:30", 210], ["+04:00", 240],
  ["+04:30", 270], ["+05:00", 300], ["+05:30", 330], ["+05:45", 345],
  ["+06:00", 360], ["+06:30", 390], ["+07:00", 420], ["+08:00", 480],
  ["+08:45", 525], ["+09:00", 540], ["+09:30", 570], ["+10:00", 600],
  ["+10:30", 630], ["+11:00", 660], ["+12:00", 720], ["+12:45", 765],
  ["+13:00", 780], ["+14:00", 840],
].map(([label, minutes]) => ({
  label: label === "+00:00" ? "UTC" : `UTC${label}`,
  value: `${minutes}`,
}));

/** One window on a time node: two bound strings, stored the way the inputs edit them. */
export interface TimeWindowBounds {
  start: string;
  end: string;
}

/**
 * The READING rule shared by both time operators. Slots 1 and 2 of the manifest
 * may each be a scalar (one window) or parallel arrays (many). A scalar is one
 * window; arrays are zipped by index. Lengths that disagree are zipped to the
 * LONGER length with "" on the short side, so the author sees the hole on the
 * canvas and can fix it, rather than a window quietly vanishing.
 */
export function readTimeWindows(
  starts: unknown,
  ends: unknown,
): TimeWindowBounds[] {
  const asText = (value: unknown): string => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    // Anything else came out of a JSON document; show it as JSON so the author
    // sees what is there rather than "[object Object]".
    return JSON.stringify(value) ?? "";
  };
  if (!Array.isArray(starts) && !Array.isArray(ends)) {
    return [{ start: asText(starts), end: asText(ends) }];
  }
  const s: unknown[] = Array.isArray(starts) ? starts : [starts];
  const e: unknown[] = Array.isArray(ends) ? ends : [ends];
  const length = Math.max(s.length, e.length);
  return Array.from({ length }, (_, i) => ({
    start: i < s.length ? asText(s[i]) : "",
    end: i < e.length ? asText(e[i]) : "",
  }));
}

/**
 * The EMISSION rule. Exactly one window emits SCALARS, byte-identical to what
 * these nodes always wrote, so no existing manifest changes when its feed is
 * reopened and re-saved. Two or more windows emit two parallel arrays.
 */
function emitTimeWindows<V>(
  windows: TimeWindowBounds[],
  convert: (bound: string) => V,
): [V | V[], V | V[]] {
  if (windows.length === 1) {
    const only = windows[0]!;
    return [convert(only.start), convert(only.end)];
  }
  return [
    windows.map((w) => convert(w.start)),
    windows.map((w) => convert(w.end)),
  ];
}

export interface HourOfDayBetweenProps extends BaseShapeProps {
  var: Field;
  /** Hours as strings "0".."23", because every select on the canvas works in strings. */
  windows: TimeWindowBounds[];
  utcOffsetMinutes: string;
}
export type HourOfDayBetweenShape = TLBaseShape<"element", HourOfDayBetweenProps>;
export interface HourOfDayBetweenFilter extends ManifestNodeMetadata {
  hour_of_day_between: [
    string,
    number | number[],
    number | number[],
    number,
  ];
}
export function makeHourOfDayBetweenFilter(
  options: HourOfDayBetweenProps,
): HourOfDayBetweenFilter {
  const [starts, ends] = emitTimeWindows(options.windows, Number);
  return {
    hour_of_day_between: [
      options.var,
      starts,
      ends,
      Number(options.utcOffsetMinutes),
    ],
    metadata: options.metadata ?? {},
  };
}
export function isHourOfDayBetweenFilter(
  filter: FilterTypes,
): filter is HourOfDayBetweenFilter {
  return "hour_of_day_between" in filter;
}

export interface DateBetweenProps extends BaseShapeProps {
  var: Field;
  /** Bound strings VERBATIM as they appear in the manifest (bare date, naive or zoned datetime). */
  windows: TimeWindowBounds[];
  utcOffsetMinutes: string;
}
export type DateBetweenShape = TLBaseShape<"element", DateBetweenProps>;
export interface DateBetweenFilter extends ManifestNodeMetadata {
  date_between: [
    string,
    string | string[],
    string | string[],
    number,
  ];
}
export function makeDateBetweenFilter(
  options: DateBetweenProps,
): DateBetweenFilter {
  const [starts, ends] = emitTimeWindows(options.windows, (bound) => bound);
  return {
    date_between: [
      options.var,
      starts,
      ends,
      Number(options.utcOffsetMinutes),
    ],
    metadata: options.metadata ?? {},
  };
}
export function isDateBetweenFilter(
  filter: FilterTypes,
): filter is DateBetweenFilter {
  return "date_between" in filter;
}

export const textSimilarityType = "text-similarity";
export interface TextSimilarityProps extends BaseShapeProps {
  var: Field;
  anchorText: string;
  modelName: string;
  operator: ComparisonOperator;
  threshold: number;
}
export type TextSimilarityShape = TLBaseShape<"element", TextSimilarityProps>;
export interface TextSimilarityFilter extends ManifestNodeMetadata {
  text_similarity: [
    string,
    {
      anchor_text: string;
      model_name: string;
    },
    ComparisonOperator,
    number,
  ];
}
export function makeTextSimilarityFilter(
  options: TextSimilarityProps,
): TextSimilarityFilter {
  return {
    metadata: options.metadata,
    text_similarity: [
      options.var,
      {
        anchor_text: options.anchorText,
        model_name: options.modelName,
      },
      options.operator,
      options.threshold,
    ],
  };
}
export function isTextSimilarityFilter(
  filter: FilterTypes,
): filter is TextSimilarityFilter {
  return "text_similarity" in filter;
}

export const inclusionOperators = ["in", "not_in"] as const;
export const inclusionOperatorOptions = [
  { label: "Include", value: "in" as const },
  { label: "Exclude", value: "not_in" as const },
];
export type InclusionOperator = (typeof inclusionOperators)[number];
export const relationshipTypes = ["follows", "followers"] as const;
export type RelationshipType = (typeof relationshipTypes)[number];
export const socialGraphType = "social-graph";
export interface SocialGraphProps extends BaseShapeProps {
  username: string;
  operator: InclusionOperator;
  direction: RelationshipType;
}
export type SocialGraphShape = TLBaseShape<"element", SocialGraphProps>;
export interface SocialGraphFilter extends ManifestNodeMetadata {
  social_graph: [string, InclusionOperator, RelationshipType];
}
export function makeSocialGraphFilter(
  options: SocialGraphProps,
): SocialGraphFilter {
  return {
    metadata: options.metadata,
    social_graph: [options.username, options.operator, options.direction],
  };
}
export function isSocialGraphFilter(
  filter: FilterTypes,
): filter is SocialGraphFilter {
  return "social_graph" in filter;
}

export const starterPackType = "starter-pack";
export interface StarterPackProps extends BaseShapeProps {
  starterPackUrl: string;
  operator: InclusionOperator;
}
export type StarterPackShape = TLBaseShape<"element", StarterPackProps>;
export interface StarterPackFilter extends ManifestNodeMetadata {
  starter_pack_member: [string, InclusionOperator];
}
export function makeStartPackFilter(
  options: StarterPackProps,
): StarterPackFilter {
  return {
    metadata: options.metadata,
    starter_pack_member: [options.starterPackUrl, options.operator],
  };
}
export function isStarterPackFilter(
  filter: FilterTypes,
): filter is StarterPackFilter {
  return "starter_pack_member" in filter;
}

export const listMemberType = "list-member";
export interface ListMemberProps extends BaseShapeProps {
  listUrl: string;
  operator: InclusionOperator;
}
export type ListMemberShape = TLBaseShape<"element", ListMemberProps>;
export interface ListMemberFilter extends ManifestNodeMetadata {
  list_member: [string, InclusionOperator];
}
export function makeListMemberFilter(
  options: ListMemberProps,
): ListMemberFilter {
  return {
    metadata: options.metadata,
    list_member: [options.listUrl, options.operator],
  };
}
export function isListMemberFilter(
  filter: FilterTypes,
): filter is ListMemberFilter {
  return "list_member" in filter;
}

/**
 * Labels a third-party atproto labeler has put on an account or a post.
 *
 * One node, two operators, because to the author it is one idea -- "posts from
 * accounts Skywatch labels X" and "posts Skywatch labels X" -- and the difference
 * is only WHAT carries the label:
 *
 * - `account_label: [labeler_did, vals, "in" | "not_in", did_field?]` is evaluated
 *   at match time anywhere in the tree, and (as `not_in` at the root) again at
 *   serve time.
 * - `post_label: [labeler_did, vals, "not_in"]` is serve-time only. A third party
 *   labels a post AFTER we have matched it, so the engines treat it as true at
 *   match time and only feeder-rs acts on it -- and only when it is a root
 *   conjunct. Inclusion by post label is not offered: it would match nothing.
 *
 * The contract is SPEC-atproto-labels-contract-2026-10.md.
 */
export const labelerLabelType = "labeler-label";
export const labelSubjects = ["account", "post"] as const;
export type LabelSubject = (typeof labelSubjects)[number];
export const labelSubjectOptions = [
  { label: "Accounts", value: "account" as const },
  { label: "Posts", value: "post" as const },
];
export interface LabelerLabelProps extends BaseShapeProps {
  labelerDid: string;
  values: string[];
  subject: LabelSubject;
  operator: InclusionOperator;
  /**
   * `account_label`'s optional 4th argument. Not offered on the node, but carried
   * so a manifest that sets it (hand-written, or from the AI designer) survives
   * being opened on the canvas.
   */
  didField?: string;
}
export type LabelerLabelShape = TLBaseShape<"element", LabelerLabelProps>;
export interface AccountLabelFilter extends ManifestNodeMetadata {
  account_label:
    | [string, string[], InclusionOperator]
    | [string, string[], InclusionOperator, string];
}
export interface PostLabelFilter extends ManifestNodeMetadata {
  post_label: [string, string[], "not_in"];
}
export function makeLabelerLabelFilter(
  options: LabelerLabelProps,
): AccountLabelFilter | PostLabelFilter {
  return { ...compileLabelerLabel(options), metadata: options.metadata };
}
export function isAccountLabelFilter(
  filter: FilterTypes,
): filter is AccountLabelFilter {
  return "account_label" in filter;
}
export function isPostLabelFilter(
  filter: FilterTypes,
): filter is PostLabelFilter {
  return "post_label" in filter;
}

export const socialListType = "social-list";
export interface SocialListProps extends BaseShapeProps {
  dids: string[];
  operator: InclusionOperator;
}
export type SocialListShape = TLBaseShape<"element", SocialListProps>;
export interface SocialListFilter extends ManifestNodeMetadata {
  social_list: [string[], InclusionOperator];
}
export function makeSocialListFilter(
  options: SocialListProps,
): SocialListFilter {
  return {
    metadata: options.metadata,
    social_list: [options.dids, options.operator],
  };
}
export function isSocialListFilter(
  filter: FilterTypes,
): filter is SocialListFilter {
  return "social_list" in filter;
}

export const entityTypes = [
  "hashtags",
  "langs",
  "urls",
  "mentions",
  "domains",
  "labels",
] as const;
export const entityTypeOptions = [
  { label: "Hashtags", value: "hashtags" as const },
  { label: "Languages", value: "langs" as const },
  { label: "URLs", value: "urls" as const },
  { label: "Mentions", value: "mentions" as const },
  { label: "Domains", value: "domains" as const },
  // Post self-labels, same path as the "Post Self Labels" attribute above --
  // NOT account labels. See the note there.
  { label: "Post Self Labels", value: "labels" as const },
];
export type EntityType = (typeof entityTypes)[number];
export const selfLabels = ["porn", "sexual", "nudity", "graphic-media"];
export const entityListType = "entity-list";
export interface EntityListProps extends BaseShapeProps {
  entities: string[];
  type: EntityType;
  operator: InclusionOperator;
}
export type EntityListShape = TLBaseShape<"element", EntityListProps>;
export type EntityListFilter =
  | ({
      entity_matches: [EntityType, string[]];
    } & ManifestNodeMetadata)
  | ({
      entity_excludes: [EntityType, string[]];
    } & ManifestNodeMetadata);
export function makeEntityListFilter(
  options: EntityListProps,
): EntityListFilter {
  if (options.operator === "in") {
    return {
      metadata: options.metadata,
      entity_matches: [
        options.type,
        options.entities.map((e) => e.replace(/^#/, "").replace(/^@/, "")),
      ],
    };
  } else {
    return {
      metadata: options.metadata,
      entity_excludes: [
        options.type,
        options.entities.map((e) => e.replace(/^#/, "").replace(/^@/, "")),
      ],
    };
  }
}
export function isEntityListFilter(
  filter: FilterTypes,
): filter is EntityListFilter {
  return "entity_matches" in filter || "entity_excludes" in filter;
}

export const magicAudienceType = "magic-audience";
export interface MagicAudienceProps extends BaseShapeProps {
  magicAudienceId: number;
  operator: InclusionOperator;
}
export type MagicAudienceShape = TLBaseShape<"element", MagicAudienceProps>;
export interface MagicAudienceFilter extends ManifestNodeMetadata {
  magic_audience: [string, InclusionOperator];
}
export function makeMagicAudienceFilter(
  options: MagicAudienceProps,
): MagicAudienceFilter {
  return {
    metadata: options.metadata,
    magic_audience: [options.magicAudienceId.toString(), options.operator],
  };
}
export function isMagicAudienceFilter(
  filter: FilterTypes,
): filter is MagicAudienceFilter {
  return "magic_audience" in filter;
}

export const modelProbabilityType = "model-probability";
export interface ModelProbabilityProps extends BaseShapeProps {
  modelName: string;
  operator: ComparisonOperator;
  threshold: number;
}
export type ModelProbabilityShape = TLBaseShape<
  "element",
  ModelProbabilityProps
>;
export interface ModelProbabilityFilter extends ManifestNodeMetadata {
  model_probability: [string, ComparisonOperator, number];
}
export function makeModelProbabilityFilter(
  options: ModelProbabilityProps,
): ModelProbabilityFilter {
  return {
    metadata: options.metadata,
    model_probability: [options.modelName, options.operator, options.threshold],
  };
}
export function isModelProbabilityFilter(
  filter: FilterTypes,
): filter is ModelProbabilityFilter {
  return "model_probability" in filter;
}

export const moderationCategories = [
  "sexual",
  "hate",
  "violence",
  "harassment",
  "self-harm",
  "sexual/minors",
  "hate/threatening",
  "violence/graphic",
  "OK",
] as const;
export const moderationCategoryDescriptions: Record<
  ModerationCategory,
  string
> = {
  sexual:
    "Content meant to arouse sexual excitement, such as the description of sexual activity, or that promotes sexual services (excluding sex education and wellness).",
  hate: "Content that expresses, incites, or promotes hate based on race, gender, ethnicity, religion, nationality, sexual orientation, disability status, or caste.",
  violence:
    "Content that promotes or glorifies violence or celebrates the suffering or humiliation of others.",
  harassment:
    "Content that may be used to torment or annoy individuals in real life, or make harassment more likely to occur.",
  "self-harm":
    "Content that promotes, encourages, or depicts acts of self-harm, such as suicide, cutting, and eating disorders.",
  "sexual/minors":
    "Sexual content that includes an individual who is under 18 years old.",
  "hate/threatening":
    "Hateful content that also includes violence or serious harm towards the targeted group.",
  "violence/graphic":
    "Violent content that depicts death, violence, or serious physical injury in extreme graphic detail.",
  OK: "Not offensive",
};
export type ModerationCategory = (typeof moderationCategories)[number];
export const contentModerationType = "content-moderation";
export interface ContentModerationProps extends BaseShapeProps {
  moderationCategory: ModerationCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type ContentModerationShape = TLBaseShape<
  "element",
  ContentModerationProps
>;
export interface ContentModerationFilter extends ManifestNodeMetadata {
  content_moderation: [ModerationCategory, ComparisonOperator, string | number];
}
export function makeContentModerationFilter(
  props: ContentModerationProps,
): ContentModerationFilter {
  return {
    metadata: props.metadata,
    content_moderation: [props.moderationCategory, props.operator, props.value],
  };
}
export function isContentModerationFilter(
  filter: FilterTypes,
): filter is ContentModerationFilter {
  return "content_moderation" in filter;
}

export const customNodeType = "custom-node";
export interface CustomNodeProps extends BaseShapeProps {
  filter: FilterTypes;
  node: {
    title: string;
    description?: string;
    component_id: number;
    version_id: number;
    /**
     * The dependency constraint this embed declares: "pinned" (this exact
     * version), "compatible" (newest non-breaking — the default for a new
     * embed) or "latest".
     *
     * Optional because every shape created before this existed has none, and
     * absence means "pinned" everywhere: client, wire and server.
     */
    dependency_intent?: DependencyIntent;
  };
  padding: Padding;
  showLogic?: boolean;
}
export type CustomNodeShape = TLBaseShape<"element", CustomNodeProps>;

export const languageAnalysisCategories = [
  "Japanese",
  "Dutch",
  "Arabic",
  "Polish",
  "German",
  "Italian",
  "Portuguese",
  "Turkish",
  "Spanish",
  "Hindi",
  "Greek",
  "Urdu",
  "Bulgarian",
  "English",
  "French",
  "Chinese",
  "Russian",
  "Thai",
  "Swahili",
  "Vietnamese",
] as const;
export type LanguageAnalysisCategory =
  (typeof languageAnalysisCategories)[number];
export const languageAnalysisType = "language-analysis";
export interface LanguageAnalysisProps extends BaseShapeProps {
  languageAnalysisCategory: LanguageAnalysisCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type LanguageAnalysisShape = TLBaseShape<
  "element",
  LanguageAnalysisProps
>;
export interface LanguageAnalysisFilter extends ManifestNodeMetadata {
  language_analysis: [
    LanguageAnalysisCategory,
    ComparisonOperator,
    string | number,
  ];
}
export function makeLanguageAnalysisFilter(
  props: LanguageAnalysisProps,
): LanguageAnalysisFilter {
  return {
    metadata: props.metadata,
    language_analysis: [
      props.languageAnalysisCategory,
      props.operator,
      props.value,
    ],
  };
}
export function isLanguageAnalysisFilter(
  filter: FilterTypes,
): filter is LanguageAnalysisFilter {
  return "language_analysis" in filter;
}

export const sentimentAnalysisCategories = [
  "Positive",
  "Negative",
  "Neutral",
] as const;
export type SentimentAnalysisCategory =
  (typeof sentimentAnalysisCategories)[number];
export const sentimentAnalysisType = "sentiment-analysis";
export interface SentimentAnalysisProps extends BaseShapeProps {
  sentimentAnalysisCategory: SentimentAnalysisCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type SentimentAnalysisShape = TLBaseShape<
  "element",
  SentimentAnalysisProps
>;
export interface SentimentAnalysisFilter extends ManifestNodeMetadata {
  sentiment_analysis: [
    SentimentAnalysisCategory,
    ComparisonOperator,
    string | number,
  ];
}
export function makeSentimentAnalysisFilter(
  props: SentimentAnalysisProps,
): SentimentAnalysisFilter {
  return {
    metadata: props.metadata,
    sentiment_analysis: [
      props.sentimentAnalysisCategory,
      props.operator,
      props.value,
    ],
  };
}
export function isSentimentAnalysisFilter(
  filter: FilterTypes,
): filter is SentimentAnalysisFilter {
  return "sentiment_analysis" in filter;
}

export const financialSentimentAnalysisCategories = [
  "Positive",
  "Negative",
  "Neutral",
] as const;
export type FinancialSentimentAnalysisCategory =
  (typeof financialSentimentAnalysisCategories)[number];

export const financialSentimentAnalysisType = "financial-sentiment-analysis";
export interface FinancialSentimentAnalysisProps extends BaseShapeProps {
  financialSentimentAnalysisCategory: FinancialSentimentAnalysisCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type FinancialSentimentAnalysisShape = TLBaseShape<
  "element",
  FinancialSentimentAnalysisProps
>;
export interface FinancialSentimentAnalysisFilter extends ManifestNodeMetadata {
  financial_sentiment_analysis: [
    FinancialSentimentAnalysisCategory,
    ComparisonOperator,
    string | number,
  ];
}
export function makeFinancialSentimentAnalysisFilter(
  props: FinancialSentimentAnalysisProps,
): FinancialSentimentAnalysisFilter {
  return {
    metadata: props.metadata,
    financial_sentiment_analysis: [
      props.financialSentimentAnalysisCategory,
      props.operator,
      props.value,
    ],
  };
}
export function isFinancialSentimentAnalysisFilter(
  filter: FilterTypes,
): filter is FinancialSentimentAnalysisFilter {
  return "financial_sentiment_analysis" in filter;
}

export const toxicityAnalysisCategories = [
  "Toxic",
  "Severe Toxicity",
  "Obscene",
  "Threat",
  "Insult",
  "Identity Hate",
] as const;
export type ToxicityAnalysisCategory =
  (typeof toxicityAnalysisCategories)[number];

export const toxicityAnalysisType = "toxicity-analysis";
export interface ToxicityAnalysisProps extends BaseShapeProps {
  toxicityAnalysisCategory: ToxicityAnalysisCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type ToxicityAnalysisShape = TLBaseShape<
  "element",
  ToxicityAnalysisProps
>;
export interface ToxicityAnalysisFilter extends ManifestNodeMetadata {
  toxicity_analysis: [
    ToxicityAnalysisCategory,
    ComparisonOperator,
    string | number,
  ];
}
export function makeToxicityAnalysisFilter(
  props: ToxicityAnalysisProps,
): ToxicityAnalysisFilter {
  return {
    metadata: props.metadata,
    toxicity_analysis: [
      props.toxicityAnalysisCategory,
      props.operator,
      props.value,
    ],
  };
}
export function isToxicityAnalysisFilter(
  filter: FilterTypes,
): filter is ToxicityAnalysisFilter {
  return "toxicity_analysis" in filter;
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
  gap: number;
}

export const logicType = "logic";
export const logicalOps = ["and", "or"] as const;
export const logicalOpOptions = [
  { label: "All of these", value: "and" as const },
  { label: "Any of these", value: "or" as const },
];
export type LogicalOp = (typeof logicalOps)[number];
export interface LogicProps extends BaseShapeProps {
  type: LogicalOp;
  padding: Padding;
}
export type LogicShape = TLBaseShape<"element", LogicProps>;

export const eachType = "each";
export interface EachProps extends BaseShapeProps {
  var: string;
  padding: Padding;
}
export type EachShape = TLBaseShape<"element", EachProps>;
export interface EachFilter extends ManifestNodeMetadata {
  each: [string, FilterTypes];
}
export function isEachFilter(filter: FilterTypes): filter is EachFilter {
  return "each" in filter;
}

export const paramCompareType = "param-compare";
export interface ParamCompareProps extends BaseShapeProps {
  var: string;
  operator: ComparisonOperator;
  compare: string;
}
export type ParamCompareShape = TLBaseShape<"element", ParamCompareProps>;
export interface ParamCompareFilter extends ManifestNodeMetadata {
  param_compare: [string, ComparisonOperator, boolean | string | number];
}
/**
 * The literal a typed comparison value compiles to.
 *
 * `true`/`false` become JSON booleans and anything numeric becomes a number, so
 * what an engine compares is NOT the string the author typed. Exported because
 * anything judging whether a comparison can ever match has to judge the value
 * after this, not before -- see `param-compare-typing.ts`.
 */
export function coerceCompareValue(
  compare: string,
): boolean | string | number {
  if (compare === "true") return true;
  if (compare === "false") return false;
  // Deliberately `Number("") === 0` and all: this is a lift of the inline
  // coercion that has always run here, and tightening it would change what
  // existing nodes compile to.
  if (!isNaN(Number(compare))) return Number(compare);
  return compare;
}

export function makeParamCompareFilter(
  props: ParamCompareProps,
): ParamCompareFilter {
  const value = coerceCompareValue(props.compare);

  return {
    metadata: props.metadata,
    param_compare: [props.var, props.operator, value],
  };
}
export function isParamCompareFilter(
  filter: FilterTypes,
): filter is ParamCompareFilter {
  return "param_compare" in filter;
}

export const targetPlaceholderType = "target-placeholder";
export type TargetPlaceholderShape = TLBaseShape<
  "element",
  {
    w: number;
  }
>;

export interface FeedProps extends BaseShapeProps {
  padding: Padding;
  order: SortingOption;
  title?: string;
  description?: string;
  custom_sort_settings?: SortingParams | null;
  hideSortSettings?: boolean;
}
export const feedType = "feed";
export type FeedShape = TLBaseShape<"element", FeedProps>;

interface CustomNodeParameterGroup {
  name: string;
  id: string;
  description?: string;
}
export interface CustomNodeRootProps extends BaseShapeProps {
  padding: Padding;
  title?: string;
  description?: string;
  customNodeParameters?: FeedParameter[];
  showCustomNodeParameters?: boolean;
  customNodeParameterGroups?: CustomNodeParameterGroup[];
}
export const customNodeRootType = "custom-node-root";
export type CustomNodeRootShape = TLBaseShape<"element", CustomNodeRootProps>;

export function isFeedShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is FeedShape {
  return shape.type === feedType;
}

export function isCustomNodeRootShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is CustomNodeRootShape {
  return shape.type === customNodeRootType;
}

export function isEachShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is EachShape {
  return shape.type === eachType;
}

export const emotionAnalysisCategories = [
  "Admiration",
  "Amusement",
  "Anger",
  "Annoyance",
  "Approval",
  "Caring",
  "Confusion",
  "Curiosity",
  "Desire",
  "Disappointment",
  "Disapproval",
  "Disgust",
  "Embarrassment",
  "Excitement",
  "Fear",
  "Gratitude",
  "Grief",
  "Joy",
  "Love",
  "Nervousness",
  "Optimism",
  "Pride",
  "Realization",
  "Relief",
  "Remorse",
  "Sadness",
  "Surprise",
  "Neutral",
] as const;
export type EmotionAnalysisCategory =
  (typeof emotionAnalysisCategories)[number];

export const emotionAnalysisType = "emotion-analysis";
export interface EmotionAnalysisProps extends BaseShapeProps {
  emotionAnalysisCategory: EmotionAnalysisCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type EmotionAnalysisShape = TLBaseShape<"element", EmotionAnalysisProps>;
export interface EmotionAnalysisFilter extends ManifestNodeMetadata {
  emotion_sentiment_analysis: [
    EmotionAnalysisCategory,
    ComparisonOperator,
    string | number,
  ];
}
export function makeEmotionAnalysisFilter(
  props: EmotionAnalysisProps,
): EmotionAnalysisFilter {
  return {
    metadata: props.metadata,
    emotion_sentiment_analysis: [
      props.emotionAnalysisCategory,
      props.operator,
      props.value,
    ],
  };
}
export function isEmotionAnalysisFilter(
  filter: FilterTypes,
): filter is EmotionAnalysisFilter {
  return "emotion_sentiment_analysis" in filter;
}

export const spamAnalysisCategories = [
  "Marketing Spam",
  "Organic Content",
] as const;
export type SpamAnalysisCategory = (typeof spamAnalysisCategories)[number];

export const spamAnalysisType = "spam-analysis";
export interface SpamAnalysisProps extends BaseShapeProps {
  spamAnalysisCategory: SpamAnalysisCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type SpamAnalysisShape = TLBaseShape<"element", SpamAnalysisProps>;
export interface SpamAnalysisFilter extends ManifestNodeMetadata {
  marketing_check: [SpamAnalysisCategory, ComparisonOperator, string | number];
}
export function makeSpamAnalysisFilter(
  props: SpamAnalysisProps,
): SpamAnalysisFilter {
  return {
    metadata: props.metadata,
    marketing_check: [props.spamAnalysisCategory, props.operator, props.value],
  };
}
export function isSpamAnalysisFilter(
  filter: FilterTypes,
): filter is SpamAnalysisFilter {
  return "marketing_check" in filter;
}

export const topicAnalysisCategories = [
  "Arts & Culture",
  "Business & Entrepreneurs",
  "Celebrity & Pop Culture",
  "Diaries & Daily Life",
  "Family",
  "Fashion & Style",
  "Film, TV & Video",
  "Fitness & Health",
  "Food & Dining",
  "Gaming",
  "Learning & Educational",
  "Music",
  "News & Social Concern",
  "Other Hobbies",
  "Relationships",
  "Science & Technology",
  "Sports",
  "Travel & Adventure",
  "Youth & Student Life",
] as const;
export type TopicAnalysisCategory = (typeof topicAnalysisCategories)[number];

export const topicAnalysisType = "topic-analysis";
export interface TopicAnalysisProps extends BaseShapeProps {
  topicAnalysisCategory: TopicAnalysisCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type TopicAnalysisShape = TLBaseShape<"element", TopicAnalysisProps>;
export interface TopicAnalysisFilter extends ManifestNodeMetadata {
  topic_analysis: [TopicAnalysisCategory, ComparisonOperator, string | number];
}
export function makeTopicAnalysisFilter(
  props: TopicAnalysisProps,
): TopicAnalysisFilter {
  return {
    metadata: props.metadata,
    topic_analysis: [props.topicAnalysisCategory, props.operator, props.value],
  };
}
export function isTopicAnalysisFilter(
  filter: FilterTypes,
): filter is TopicAnalysisFilter {
  return "topic_analysis" in filter;
}

export const notesType = "notes";

export const standardCategories = [
  "Academic & Intellectual",
  "Adult & Sexual Content",
  "AI & Machine Learning",
  "Animals",
  "Arts & Creative",
  "Aviation & Maritime",
  "Data & Computing",
  "Entertainment & Culture",
  "Film & TV",
  "Food & Beverages",
  "Food & Lifestyle",
  "Game Development",
  "Gaming",
  "Healthcare & Medicine",
  "Medical Education",
  "Medical Specialties",
  "Music",
  "Nature & Outdoors",
  "News & Media",
  "Politics",
  "Programming",
  "Society & Culture",
  "Software Development",
  "Sports",
  "Visual Arts",
];
type StandardCategory = (typeof standardCategories)[number];

export const textCategorizationType = "text-categorization";
export interface TextCategorizationProps extends BaseShapeProps {
  tag: StandardCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type TextCategorizationShape = TLBaseShape<
  "element",
  TextCategorizationProps
>;
export interface TextCategorizationFilter extends ManifestNodeMetadata {
  text_arbitrary: [StandardCategory, ComparisonOperator, string | number];
}
export function makeTextCategorizationFilter(
  props: TextCategorizationProps,
): TextCategorizationFilter {
  return {
    metadata: props.metadata,
    text_arbitrary: [props.tag, props.operator, props.value],
  };
}
export function isTextCategorizationFilter(
  filter: FilterTypes,
): filter is TextCategorizationFilter {
  return "text_arbitrary" in filter;
}

export const imageModerationCategories = ["NSFW", "SFW"] as const;
export type ImageModerationCategory =
  (typeof imageModerationCategories)[number];

export const imageModerationType = "image-moderation";
export interface ImageModerationProps extends BaseShapeProps {
  imageModerationCategory: ImageModerationCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type ImageModerationShape = TLBaseShape<"element", ImageModerationProps>;
export interface ImageModerationFilter extends ManifestNodeMetadata {
  image_nsfw: [ImageModerationCategory, ComparisonOperator, string | number];
}
export function makeImageModerationFilter(
  props: ImageModerationProps,
): ImageModerationFilter {
  return {
    metadata: props.metadata,
    image_nsfw: [props.imageModerationCategory, props.operator, props.value],
  };
}
export function isImageModerationFilter(
  filter: FilterTypes,
): filter is ImageModerationFilter {
  return "image_nsfw" in filter;
}

export const imageCategorizationType = "image-categorization";
export interface ImageCategorizationProps extends BaseShapeProps {
  tag: StandardCategory;
  operator: ComparisonOperator;
  value: number | string;
}
export type ImageCategorizationShape = TLBaseShape<
  "element",
  ImageCategorizationProps
>;
export interface ImageCategorizationFilter extends ManifestNodeMetadata {
  image_arbitrary: [StandardCategory, ComparisonOperator, string | number];
}
export function makeImageCategorizationFilter(
  props: ImageCategorizationProps,
): ImageCategorizationFilter {
  return {
    metadata: props.metadata,
    image_arbitrary: [props.tag, props.operator, props.value],
  };
}
export function isImageCategorizationFilter(
  filter: FilterTypes,
): filter is ImageCategorizationFilter {
  return "image_arbitrary" in filter;
}

export const postTypeType = "post-type";
export const postTypeOperators = ["reply", "quote"] as const;
export type PostTypeOperator = (typeof postTypeOperators)[number];

export interface PostTypeProps extends BaseShapeProps {
  operator: InclusionOperator;
  post_type: PostTypeOperator;
}

export type PostTypeShape = TLBaseShape<"element", PostTypeProps>;

export interface PostTypeFilter extends ManifestNodeMetadata {
  post_type: [InclusionOperator, [PostTypeOperator]];
}

export function makePostTypeFilter(options: PostTypeProps): PostTypeFilter {
  return {
    metadata: options.metadata,
    post_type: [options.operator, [options.post_type]],
  };
}

export function isPostTypeFilter(
  filter: FilterTypes,
): filter is PostTypeFilter {
  return "post_type" in filter;
}

// The shape TYPE stays "openai" deliberately, even though the operator it now emits is
// `text_predicate`. tldraw persists `type` inside every saved canvas record, so renaming it would
// orphan the shape in every feed that already contains one. The user-facing label is what changed.
//
// So this node answers to THREE names on purpose, and none of them can be made to agree:
// the shape type is "openai", the operator on the wire is `text_predicate`, and the label an
// author reads is "Natural Language". Only the last one is a naming decision -- the other two
// are persisted formats. Do not "fix" the mismatch.
export const openaiType = "openai";
export interface OpenAIProps extends BaseShapeProps {
  prompt: string;
  operator: ComparisonOperator;
  threshold: number;
  /**
   * What this condition answers for a post it could not score because the feed was over its
   * hourly quota. `false` (the default) means the condition matches nothing, so the feed goes
   * quiet; `true` means it stops filtering and admits everything.
   *
   * Optional because every canvas saved before this existed has no value for it, and absent has
   * to keep meaning the safe answer.
   */
  overQuota?: boolean;
}
export type OpenAIShape = TLBaseShape<"element", OpenAIProps>;

/**
 * Reads BOTH operator names and writes only `text_predicate`.
 *
 * `openai_prompt` no longer involves OpenAI — it is scored by the in-cluster adjudicator, same
 * model and same cost as `text_predicate` — so the two are one operator with two names. Accepting
 * both on read means the feeds already carrying `openai_prompt` keep loading; emitting only
 * `text_predicate` means each one migrates the next time its author saves.
 */
export interface OpenAIFilter extends ManifestNodeMetadata {
  openai_prompt?: [string, ComparisonOperator, number];
  text_predicate?: [string, ComparisonOperator, number] | [string, ComparisonOperator, number, boolean];
}
export function makeOpenAIFilter(props: OpenAIProps): OpenAIFilter {
  return {
    metadata: props.metadata,
    // The fourth element is only emitted when the author chose the non-default, so a manifest
    // stays byte-identical to the three-argument form unless they actually asked for something.
    text_predicate: props.overQuota
      ? [props.prompt, props.operator, props.threshold, true]
      : [props.prompt, props.operator, props.threshold],
  };
}
export function isOpenAIFilter(filter: FilterTypes): filter is OpenAIFilter {
  return "openai_prompt" in filter || "text_predicate" in filter;
}
/** The args, whichever key they arrived under. */
export function openAIFilterArgs(
  filter: OpenAIFilter,
): [string, ComparisonOperator, number, boolean] {
  const args = filter.text_predicate ?? filter.openai_prompt;
  if (!args) {
    throw new Error("an OpenAIFilter must carry text_predicate or openai_prompt");
  }
  return [args[0], args[1], args[2], args.length > 3 ? Boolean(args[3]) : false];
}

// --- "Ask a Question": Jev-backed typed questions --------------------------------------
//
// A sibling of the "Natural Language" node, NOT a replacement. That one asks a yes/no
// predicate of a GPU cross-encoder; this asks one of three TYPED questions of a much
// cheaper model and thresholds the number that comes back. Both stay: they are different
// engines with different budgets, and no existing feed changes meaning.
//
// Unlike `openai`, the shape type and the operator name agree here (`text-question` /
// `text_question`) because this node is new and has no persisted history to preserve.

export const textQuestionType = "text-question";

export const questionKinds = ["noul", "choice", "score"] as const;
export type QuestionKind = (typeof questionKinds)[number];

/** One option of a Choice question: the label the model picks, and what it means. */
interface QuestionOption {
  name: string;
  description: string;
}

export interface TextQuestionProps extends BaseShapeProps {
  kind: QuestionKind;
  instructions: string;
  /**
   * Choice options. Kept in props even while `kind` is not "choice" so switching kind to
   * look at another mode and back does not silently discard what the author typed.
   */
  options: QuestionOption[];
  /** Which option's probability the condition reads. Choice only. */
  select: string;
  /** Score levels, in order. Order is meaning: level 0 is the first entry. */
  levels: string[];
  operator: ComparisonOperator;
  /**
   * For noul and choice this is a probability in 0-1. For score it is a LEVEL INDEX, not
   * a percentage -- a three-level rubric is thresholded against 0, 1 or 2. Jev's score
   * levels are weakly calibrated numerically, so rescaling them into a percentage would
   * invent precision the model does not have.
   */
  threshold: number;
  /** What this condition answers for a post refused by the hourly budget. */
  overQuota?: boolean;
}
export type TextQuestionShape = TLBaseShape<"element", TextQuestionProps>;

/** The question object as it goes on the wire. Mirrors Jev's own shape. */
type QuestionBody =
  | { type: "noul"; instructions: string }
  | {
      type: "choice";
      instructions: string;
      criteria: Record<string, string | null>;
      select: string;
    }
  | { type: "score"; instructions: string; criteria: string[] };

interface TextQuestionFilter extends ManifestNodeMetadata {
  text_question:
    | [QuestionBody, ComparisonOperator, number]
    | [QuestionBody, ComparisonOperator, number, boolean];
}

function makeQuestionBody(props: TextQuestionProps): QuestionBody {
  if (props.kind === "choice") {
    return {
      type: "choice",
      instructions: props.instructions,
      // An option with no description is a legitimate option with no extra detail, and
      // the engines accept null for exactly that. Blank-named rows are dropped rather
      // than sent: an empty key cannot be selected and would fail validation anyway.
      criteria: Object.fromEntries(
        props.options
          .filter((option) => option.name.trim() !== "")
          .map((option) => [
            option.name.trim(),
            option.description.trim() === "" ? null : option.description.trim(),
          ]),
      ),
      select: props.select,
    };
  }
  if (props.kind === "score") {
    return {
      type: "score",
      instructions: props.instructions,
      criteria: props.levels.filter((level) => level.trim() !== ""),
    };
  }
  return { type: "noul", instructions: props.instructions };
}

export function makeTextQuestionFilter(
  props: TextQuestionProps,
): TextQuestionFilter {
  const question = makeQuestionBody(props);
  return {
    metadata: props.metadata,
    // The fourth element is emitted only when the author chose the non-default, so a
    // manifest stays byte-identical to the three-argument form unless they asked for
    // something.
    text_question: props.overQuota
      ? [question, props.operator, props.threshold, true]
      : [question, props.operator, props.threshold],
  };
}

export function isTextQuestionFilter(
  filter: FilterTypes,
): filter is TextQuestionFilter {
  return "text_question" in filter;
}

/** Props for the canvas, read back out of a saved filter. */
export function textQuestionProps(filter: TextQuestionFilter): {
  kind: QuestionKind;
  instructions: string;
  options: QuestionOption[];
  select: string;
  levels: string[];
  operator: ComparisonOperator;
  threshold: number;
  overQuota: boolean;
} {
  const [question, operator, threshold, overQuota] = filter.text_question;
  // Widened deliberately: this reads a SAVED manifest, which can carry a kind this build
  // does not know (a feed authored by a newer UI, or hand-edited JSON). The declared type
  // says that cannot happen; the file on disk disagrees.
  const rawKind = (question as { type?: string } | undefined)?.type;
  const kind: QuestionKind = questionKinds.includes(rawKind as QuestionKind)
    ? (rawKind as QuestionKind)
    : "noul";
  return {
    kind,
    instructions: question?.instructions ?? "",
    options:
      kind === "choice"
        ? Object.entries(
            (question as { criteria?: Record<string, string | null> }).criteria ?? {},
          ).map(([name, description]) => ({ name, description: description ?? "" }))
        : [],
    select: kind === "choice" ? ((question as { select?: string }).select ?? "") : "",
    levels:
      kind === "score"
        ? ((question as { criteria?: string[] }).criteria ?? []).slice()
        : [],
    operator,
    threshold,
    overQuota: Boolean(overQuota),
  };
}

export type CustomShapes =
  | SimpleRegexShape
  | MultiRegexShape
  | LogicShape
  | AttributeCompareShape
  | TextSimilarityShape
  | SocialGraphShape
  | StarterPackShape
  | ListMemberShape
  | LabelerLabelShape
  | SocialListShape
  | MagicAudienceShape
  | ModelProbabilityShape
  | ContentModerationShape
  | EmbedShape
  | EntityListShape
  | LanguageAnalysisShape
  | SentimentAnalysisShape
  | FinancialSentimentAnalysisShape
  | ToxicityAnalysisShape
  | EmotionAnalysisShape
  | SpamAnalysisShape
  | TopicAnalysisShape
  | TextCategorizationShape
  | ImageModerationShape
  | ImageCategorizationShape
  | PostTypeShape
  | OpenAIShape
  | EachShape
  | ParamCompareShape
  | RatioCompareShape
  | TextQuestionShape
  | HourOfDayBetweenShape
  | DateBetweenShape;
export const pinnableShapes = [
  notesType,
  embedType,
  multiRegexType,
  simpleRegexType,
  logicType,
  attributeCompareType,
  ratioCompareType,
  textQuestionType,
  hourOfDayBetweenType,
  dateBetweenType,
  textSimilarityType,
  socialGraphType,
  starterPackType,
  listMemberType,
  labelerLabelType,
  magicAudienceType,
  socialListType,
  contentModerationType,
  modelProbabilityType,
  entityListType,
  languageAnalysisType,
  sentimentAnalysisType,
  financialSentimentAnalysisType,
  toxicityAnalysisType,
  emotionAnalysisType,
  spamAnalysisType,
  topicAnalysisType,
  textCategorizationType,
  imageModerationType,
  imageCategorizationType,
  postTypeType,
  customNodeType,
  paramCompareType,
  openaiType,
  eachType,
];

export type LayoutBinding = TLBaseBinding<
  "layout",
  {
    index: IndexKey;
    placeholder: boolean;
    dimensions?: {
      w: number;
      h: number;
      x: number;
      y: number;
    };
  }
>;

export function isLogicShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is LogicShape {
  return shape.type === logicType;
}

export function isCustomNodeShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is CustomNodeShape {
  return shape.type === customNodeType;
}

export function getMinWForLogic(
  editor: Editor,
  shape: LogicShape | FeedShape,
): number {
  const bindings = editor
    .getBindingsFromShape<LayoutBinding>(shape, "layout")
    .sort((a, b) => (a.props.index > b.props.index ? 1 : -1));

  return Math.max(
    shape.props.minW || 0,
    ...bindings.map((b) => {
      const shape = editor.getShape(b.toId);

      if (shape && "minW" in shape.props) {
        return shape.props.minW as number;
      }

      if (shape && isLogicShape(shape)) {
        return (
          getMinWForLogic(editor, shape) +
          shape.props.padding.left +
          shape.props.padding.right
        );
      }

      return 0;
    }),
  );
}

export const patterns = [
  "checkerboard",
  "dots",
  "lines",
  "zig zag",
  "cow",
] as const;
export type Pattern = (typeof patterns)[number];

export const paramType = T.or(
  T.object({
    name: T.string,
    displayName: T.string.optional(),
    group: T.string.optional(),
    description: T.string,
    exampleValue: T.string,
    value: T.string.optional(),
    type: T.literal("select"),
    options: T.arrayOf(T.string),
    labels: T.arrayOf(T.string).optional(),
  }),
  T.or(
    T.object({
      name: T.string,
      displayName: T.string.optional(),
      group: T.string.optional(),
      description: T.string,
      exampleValue: T.arrayOf(T.string),
      value: T.arrayOf(T.string).optional(),
      type: T.literal("list"),
    }),
    T.or(
      T.object({
        name: T.string,
        displayName: T.string.optional(),
        group: T.string.optional(),
        description: T.string,
        exampleValue: T.boolean,
        value: T.boolean.optional(),
        type: T.literal("toggle"),
      }),
      T.or(
        T.object({
          name: T.string,
          displayName: T.string.optional(),
          group: T.string.optional(),
          description: T.string,
          exampleValue: T.string,
          value: T.string.optional(),
          type: T.literalEnum("input").optional(),
        }),
        T.or(
          T.object({
            name: T.string,
            displayName: T.string.optional(),
            group: T.string.optional(),
            description: T.string,
            exampleValue: T.number,
            value: T.number.optional(),
            type: T.literalEnum("number"),
            isPercentage: T.boolean.optional(),
          }),
          T.object({
            name: T.string,
            displayName: T.string.optional(),
            group: T.string.optional(),
            description: T.string,
            exampleValue: T.string,
            value: T.string.optional(),
            type: T.literalEnum("regex"),
          }),
        ),
      ),
    ),
  ),
);

/**
 * The fields a shape's `metadata` prop may carry.
 *
 * Kept as a named object so `metadataKeys` can be derived from it: this
 * validator is STRICT -- an undeclared property is a ValidationError that takes
 * the whole canvas down -- while the manifest leaf it is populated from is a
 * wire format that grows a field whenever the feature does. `buildBindings`
 * filters incoming metadata to these keys for exactly that reason.
 */
const metadataFields = {
  title: T.string.optional(),
  description: T.string.optional(),
  pattern: T.literalEnum(...patterns).optional(),
  color: T.literalEnum(
    "slate",
    "tomato",
    "red",
    "ruby",
    "crimson",
    "pink",
    "plum",
    "purple",
    "violet",
    "iris",
    "indigo",
    "blue",
    "cyan",
    "teal",
    "jade",
    "green",
    "grass",
    "bronze",
    "gold",
    "brown",
    "orange",
    "amber",
    "yellow",
    "lime",
    "mint",
    "sky",
  ).optional(),
  version_id: T.number.optional(),
  component_id: T.number.optional(),
  component_name: T.string.optional(),
  isCustomNode: T.boolean.optional(),
  customNodeAutoUpdate: T.boolean.optional(),
  customNodeParameters: T.arrayOf(paramType).optional(),
  customNodeParameterGroups: T.arrayOf(
    T.object({
      name: T.string,
      id: T.string,
      description: T.string.optional(),
    }),
  ).optional(),
  mustIncludeInSimpleFeed: T.boolean.optional(),
  // Declared here as well as on the custom node's `node` prop, because a leaf's
  // metadata carries it and this schema is what every shape validates that
  // metadata against. Omitting it is what made expanding an embedded custom
  // node's preview throw "ValidationError: Unexpected property" once an author
  // set an intent: the expanded body's root metadata IS the leaf's metadata.
  dependency_intent: T.literalEnum("pinned", "compatible", "latest").optional(),
};

export const metadataType = T.object(metadataFields).optional().nullable();

/** The property names `metadataType` accepts. */
export const metadataKeys = Object.keys(metadataFields);

/* ==========================================================================
 * Page layers and audience blocks
 *
 * A feed's page is divided between up to ten LAYERS, each an ordering with a
 * share of the page and, optionally, an audience (filters that remove posts,
 * boosts that move them up). These used to be one scrolling form inside the
 * Feed shape; they are canvas nodes now.
 *
 * 🔴 A layer is NOT a condition. It is never bound under the Feed, never reaches
 * `buildTree`, and never appears in `manifest.filter` — an unknown operator
 * there is rejected recursively by the matchers and takes the feed dark. Layers
 * are free roots on the page (like Notes), collected by type in canvas order by
 * `buildLensList`, and written to `custom_sort_settings.lenses`. Audience blocks
 * pin INTO a layer via the same `layout` binding the Logic node uses, and into
 * nothing else — see each util's `canBind`.
 * ========================================================================== */

export const layerType = "layer";
export interface LayerProps extends BaseShapeProps {
  padding: Padding;
  /**
   * The layer's durable id — the per-lane CURSOR KEY. Absent for a layer stored
   * before ids existed (the serve path synthesises `"{i}:{sort_order}"` for
   * those, byte-identical to their existing cursor key). Never write one onto
   * such a layer: it would move its readers' scroll. Minted for layers a
   * creator adds.
   */
  layerId?: string;
  /** Weight on the wire; the editor keeps the list summing to 1. */
  share: number;
  sort_order: LensOrdering;
  /**
   * This layer's own ORDERING settings — window, decay, multipliers — which
   * override the feed's for this lane's query. Empty means "inherit the feed's".
   * Open-ended on purpose: `feed_lenses.LANE_KEYS` is only {share, sort_order,
   * id, gym}, so every other key a stored lane carries is one of these.
   */
  settings: Record<string, unknown>;
  /**
   * Whether the ordering's parameters (multipliers, decay, window) are shown.
   * Closed by default: most layers run on the defaults, and the grid is the
   * tallest thing in the block. Optional so layers stored before it open.
   */
  showSettings?: boolean;
  /** Whether the thresholds editor is shown. Closed by default, like the parameters. */
  showThresholds?: boolean;
  /**
   * This layer's own count thresholds — the and/or of like/reply/repost gates
   * that used to sit on the Feed. Per lane on the wire: `feed_lenses` merges a
   * lane's keys over the feed's, and `thresholds` is one of them.
   */
  thresholds?: Thresholds | null;
  /** The layer whose ordering the Feed Gym chooses. At most one per feed. */
  gym: boolean;
  /** Boost-plan dials, per LAYER not per boost block: how far the boost may move the page, and how several boost signals combine. */
  boostStrength: number;
  boostCombine: "max" | "sum";
  /**
   * What the layer's filters do when they leave the page empty. Per LAYER: v2
   * wrote it per filter and composed to the strictest, which is one answer
   * about the page, and v3 says so. Written back onto every v2 filter when the
   * tree is flat enough to be v2.
   */
  onEmpty?: AudienceOnEmpty;
  /**
   * A lens this build can read but cannot draw as blocks — a v1 blend or a
   * preset name. Carried through untouched and shown read-only behind a
   * Convert button, never auto-converted: silently rewriting a saved manifest
   * when someone opens the editor is how a draft/live divergence becomes a
   * surprise publish. Cleared the moment the layer gets an audience block.
   */
  legacyLens?: unknown;
}
export type LayerShape = TLBaseShape<"element", LayerProps>;

export const audienceFilterType = "audience-filter";
export interface AudienceFilterProps extends BaseShapeProps {
  facet: BlendableLensFacet;
  /** "at least N of the people they follow". Countable facets only. */
  min?: number;
  /** Legacy, tolerated for snapshots from the first cut; never read. See audience-filter.tsx. */
  on_empty?: AudienceOnEmpty;
}
export type AudienceFilterShape = TLBaseShape<"element", AudienceFilterProps>;

export const audienceBoostType = "audience-boost";
export interface AudienceBoostProps extends BaseShapeProps {
  facet: BlendableLensFacet;
  weight: number;
}
export type AudienceBoostShape = TLBaseShape<"element", AudienceBoostProps>;

/**
 * "Any / all of these" over audience filters — the v3 contract's `and`/`or`.
 *
 * Its own shape rather than the Logic node with a flag: Logic's `canBind`
 * accepts every match condition, and a match leaf inside an audience group
 * (or an audience leaf inside a match group) must be un-droppable, not merely
 * un-compilable. Boosts never pin inside one: they are not boolean.
 */
export const audienceLogicType = "audience-logic";
export interface AudienceLogicProps extends BaseShapeProps {
  type: LogicalOp;
  padding: Padding;
}
export type AudienceLogicShape = TLBaseShape<"element", AudienceLogicProps>;

/**
 * Each ordering has its own colour, so a page split three ways reads as three
 * things at a glance and a layer's kind is legible from across the canvas. The
 * audience blocks inside keep their own colours (green/plum/blue) regardless.
 */
export const orderingVariants = {
  new: "green",
  trending: "amber",
  blend: "purple",
  old: "slate",
  hackernews: "orange",
  wilson: "blue",
} as const satisfies Record<LensOrdering, string>;
/** The Gym's slice is a different KIND of thing — Graze chooses what fills it — so it is striped yellow, not an ordering colour. */
export const gymLayerVariant = "yellow" as const;

/** What may be pinned inside a layer. Notes included, as everywhere else. */
export const layerChildShapes: readonly string[] = [
  audienceFilterType,
  audienceBoostType,
  audienceLogicType,
  notesType,
];
/** What may be pinned inside an audience group: filters and groups, never a boost. */
export const audienceGroupChildShapes: readonly string[] = [
  audienceFilterType,
  audienceLogicType,
  notesType,
];

export function isLayerShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is LayerShape {
  return shape.type === layerType;
}
export function isAudienceFilterShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is AudienceFilterShape {
  return shape.type === audienceFilterType;
}
export function isAudienceBoostShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is AudienceBoostShape {
  return shape.type === audienceBoostType;
}
export function isAudienceLogicShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is AudienceLogicShape {
  return shape.type === audienceLogicType;
}
